import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Section, signed } from "./primitives";

const STATUS_STYLE: Record<string, string> = {
  "ALIGNED": "border-[#34D399]/40 text-[#34D399]",
  "LAYER INDEX LEADS": "border-[#A78BFA]/40 text-[#A78BFA]",
  "FEAR & GREED LEADS": "border-[#F7931A]/40 text-[#F7931A]",
  "DIVERGING": "border-[#EF4444]/40 text-[#F87171]",
};

const DivDot = (props: any) => {
  const { cx, cy, payload, days } = props;
  if (cx == null || cy == null || payload?.li == null || !days.has(payload.date)) return null;
  return (
    <path d={`M ${cx} ${cy - 5} L ${cx + 5} ${cy} L ${cx} ${cy + 5} L ${cx - 5} ${cy} Z`}
      fill="#05D1E5" opacity={0.9} data-testid={`li-divergence-mark-${payload.date}`} />
  );
};

const CmpTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0d0e14] px-3 py-2.5 text-xs">
      <p className="nl-mono text-[10px] text-gray-400">{p.date}</p>
      {p.li != null && <p className="text-[#A78BFA] font-bold mt-0.5">Layer Index {p.li}</p>}
      <p className="text-[#F7931A] font-bold">Fear &amp; Greed {p.fg}</p>
      {p.li != null && (
        <p className="text-gray-400 mt-0.5">Spread {signed(p.li - p.fg)}</p>
      )}
    </div>
  );
};

export const FearGreedCompare = ({ fearGreed, history, divergence, divergenceDays, methodology }: {
  fearGreed: any; history: any[]; divergence: any; divergenceDays: string[]; methodology: any;
}) => {
  const divSet = useMemo(() => new Set(divergenceDays || []), [divergenceDays]);
  const rows = useMemo(() => {
    const liByDate: Record<string, number> = {};
    for (const h of history) liByDate[h.date] = h.value;
    const fg30 = (fearGreed?.history || []).slice(-30);
    return fg30.map((f: any) => ({ date: f.date, fg: f.value, li: liByDate[f.date] ?? null }));
  }, [fearGreed, history]);
  const div = methodology?.divergence || {};

  return (
    <Section num="04" title="Layer Index vs Fear & Greed" sub="Conventional sentiment, compared"
      testid="li-fg-section">
      {!fearGreed?.available ? (
        <p className="text-sm text-gray-500" data-testid="li-fg-unavailable">
          The Fear &amp; Greed comparison is temporarily unavailable — the provider could not be reached.
          Nothing is estimated in its place.
        </p>
      ) : (
        <>
          {divergence && (
            <div className="grid grid-cols-3 lg:grid-cols-[auto_auto_auto_minmax(0,1fr)] gap-x-8 lg:gap-x-12 gap-y-4 items-center mb-7"
              data-testid="li-divergence-strip">
              <div>
                <p className="nl-mono text-[9px] uppercase tracking-[0.22em] text-gray-600">Layer Index</p>
                <p className="nl-sg text-2xl font-bold text-[#A78BFA] mt-0.5" data-testid="li-div-li">
                  {divergence.layer_index}
                </p>
              </div>
              <div>
                <p className="nl-mono text-[9px] uppercase tracking-[0.22em] text-gray-600">Fear &amp; Greed</p>
                <p className="nl-sg text-2xl font-bold text-[#F7931A] mt-0.5" data-testid="li-div-fg">
                  {divergence.fear_greed}
                </p>
              </div>
              <div>
                <p className="nl-mono text-[9px] uppercase tracking-[0.22em] text-gray-600">Spread</p>
                <p className="nl-sg text-2xl font-bold text-white mt-0.5" data-testid="li-div-spread">
                  {signed(divergence.spread)}
                </p>
              </div>
              <div className="col-span-3 lg:col-span-1">
                <span className={`inline-block nl-mono text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded border ${
                  STATUS_STYLE[divergence.status] || "border-white/20 text-gray-300"}`}
                  data-testid="li-div-status">
                  {divergence.status}
                </span>
                <p className="text-[12.5px] text-gray-400 leading-snug mt-2 max-w-xl" data-testid="li-div-interpretation">
                  {divergence.interpretation}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3">
            <span className="flex items-center gap-1.5 nl-mono text-[10px] text-gray-400">
              <span className="h-[3px] w-5 rounded-full bg-[#A78BFA]" /> Layer Index
            </span>
            <span className="flex items-center gap-1.5 nl-mono text-[10px] text-gray-400">
              <span className="h-[3px] w-5 rounded-full bg-[#F7931A]" /> Fear &amp; Greed
            </span>
            <span className="flex items-center gap-1.5 nl-mono text-[10px] text-gray-400">
              <span className="h-2 w-2 rotate-45 bg-[#05D1E5]" /> Divergence ≥ {div.chart_mark_threshold ?? 15} pts
            </span>
          </div>
          <div className="h-64 sm:h-72" data-testid="li-fg-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false}
                  tickFormatter={(v: string) => v.slice(5)} interval="preserveStartEnd" minTickGap={30} />
                <YAxis domain={[0, 100]} stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false}
                  ticks={[0, 20, 40, 60, 80, 100]} />
                <ReferenceLine y={50} stroke="#ffffff10" strokeDasharray="4 4" />
                <Tooltip content={<CmpTooltip />} />
                <Line type="monotone" dataKey="fg" stroke="#F7931A" strokeWidth={1.5} strokeOpacity={0.85}
                  dot={false} isAnimationActive={false} connectNulls={false} />
                <Line type="monotone" dataKey="li" stroke="#A78BFA" strokeWidth={2.2}
                  dot={<DivDot days={divSet} />} isAnimationActive={false} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="nl-mono text-[9.5px] text-gray-600 mt-3 leading-relaxed" data-testid="li-fg-attribution">
            Fear &amp; Greed data: Alternative.me · Layer Index: NewsLayer proprietary methodology
            v{methodology?.version || "1.0"}. Divergence rule: a spread of ≥ {div.spread_threshold ?? 10} pts
            determines which signal leads; opposite {div.trend_window_days ?? 7}-day moves of
            ≥ {div.trend_threshold ?? 5} pts each flag DIVERGING. The Layer Index line starts where recorded
            history begins — missing dates are never backfilled or interpolated.
          </p>
        </>
      )}
    </Section>
  );
};
